import React, {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Card,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Skeleton,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import {
    AddOutlined,
    DeleteOutlined,
    EditOutlined,
    HistoryOutlined,
    InfoOutlined,
    KeyboardArrowLeftRounded,
    Redo,
    RestoreOutlined,
    Undo
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {InputBaseProps} from "@mui/material/InputBase";
import {Link, useHistory, useParams} from "react-router-dom";
import PurchasePlanningInformationImportDialog, {PurchasePlanningInformationImportDialogProps} from "./import-dialog";
import PurchasePlanningChangesDialog from "./changes-dialog";
import PurchasePlanningSaveDialog, {PurchasePlanningSaveEntry} from "./save-dialog";
import PurchasePlanningRemovedVendorsDialog from "./removed-vendors-dialog";
// @ts-ignore
import {ReactComponent as Excel} from '../../../../../../assets/images/excel.svg';
import './index.scss';
import classNames from "classnames";
import PurchasePlanningDetailsQuotesDialog, {PurchasePlanningInformationQuotesDialogProps} from "./quotes-dialog";
import PurchasePlanningInformationUpdateNameDialog, {
    PurchasePlanningInformationUpdateNameDialogProps
} from "./update-name-dialog";
import BizProductionApiService, {
    ApiServiceModels
} from "../../../../../../core/services/api_service/production_api_service";
import Utils, {formatMoney} from "../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useAppDispatch} from "../../../../../redux";
import {routeFunctions, routes} from "../../../../../../core/models/static/routes/app-routes";
import DateUtils from "../../../../../../core/services/date-utils";
import axios, {CancelToken} from "axios";
import {History} from "history";
import PartnerDataGrid, {
    DataGridCell,
    DataGridCellContent,
    dataGridCellTextProps,
    DataGridColumn,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridRow
} from "../../../../../containers/data-grid";
import {PanelFooter} from "../../../../../components/layout";
import SearchInput from "../../../../../components/base/input/search_input";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import PartAutocomplete, {PartOption} from "../../../../../components/text-fields/autocomplete/part-autocomplete";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import usePurchasingKeyController from "../../../../../hooks/use-purchasing-key";
import PurchasePlanningDetailsWarrantiesDialog, {
    PurchasePlanningInformationWarrantiesDialogProps
} from "./warranty-dialog";
import ChangeOrderDialog from "../../../../../components/dialogs/change-order";
import RouteLeavingGuard from "../../../../../components/base/route-leaving-guard";
import {
    distributedQtyColumnTitle,
    droppedVendorDistributions,
    DroppedVendorDistribution,
    mergeServerRows,
    PlanningDraftChangeSegment,
    PlanningDraftChangeTone,
    VendorDistribution,
} from "./planning-draft";
import {usePlanningDraft} from "./use-planning-draft";

interface DynamicColumn<GetDataResult = string | number> {
    name?: string;
    description?: string;
    title?: string;
    titleElement?: React.ReactNode;
    type?: DataGridColumnTypes;
    getData: (data: DataItemRecord) => GetDataResult;
    width?: number;
    getQuoteDialogInformation?: ((data: DataItemRecord) => {
        partNumber: string;
        year: number;
    });
}


interface DataItemRecord extends ApiServiceModels.PurchasePlanningDataItem {
    _id: string; // this is locally added
    _changed: boolean; // this is locally added
    /** Session-only. Not sent to Save or Calculate until the backend can store it. */
    vendorDistributions?: VendorDistribution[];
}

export interface Data extends Omit<ApiServiceModels.GetPurchasePlanningInformationResponseDs, 'id'> {
    dataItems: DataItemRecord[];
    id?: string;
}

const dataGridColumns = {
    remove: 'remove',
    index: 'index',
    qty: 'qty',
    forecastedQty: 'forecastedQty',
    partNo: 'partNo',
    category: 'category',
    note: 'note',
}

const columnNames = {
    availability: (whse: string) => `${whse}-availability`,
    vendor: (vendor: string) => `${vendor}-vendor`,
    sales: (year: number) => `sales-${year}`,

    onOrderQty: (vendor: string) => `${vendor}-on-order-qty`,
    onOrderPo: (vendor: string) => `${vendor}-on-order-po`,
    onOrderEta: (vendor: string) => `${vendor}-on-order-eta`,
    partNoFormat: (vendor: string) => `${vendor}-part-no`,
    price: (vendor: string) => `${vendor}-price`,
    quotes: (year: number) => `quotes-${year}`,
    lostSalesYtd: (year: number) => `lost-sales-ytd-${year}`,
    salesYtd: (year: number) => `sales-ytd-${year}`,
    avgMonthlySalesYtd: (year: number) => `avg-monthly-sales-ytd-${year}`,
}

const AutocompleteClass = "part-number-autocomplete";


const initialChangeOrderDialog = {
    open: false,
    close: () => void 0,
    orderIndex: 0,
    length: 0,
    reorder: async () => void 0,
    message: ""
}

const initialQuoteDialogState: PurchasePlanningInformationQuotesDialogProps = {open: false, close: () => void 0}

const initialWarrantyDialogState: PurchasePlanningInformationWarrantiesDialogProps = {open: false, close: () => void 0}

const initialImportDialogState: PurchasePlanningInformationImportDialogProps = {
    open: false,
    close: () => void 0,
    onImport: () => void 0
}

const initialUpdateNameDialogState: PurchasePlanningInformationUpdateNameDialogProps = {
    open: false,
    close: () => void 0,
    name: '',
    update: async () => true
};

const DETAIL_UPDATE_BATCH_SIZE = 5;
const DETAIL_UPDATE_BATCH_GAP_MS = 300;

/**
 * Pauses between detail-update batches so a large plan does not flood the server.
 */
const wait = (ms: number) => new Promise<void>(resolve => {
    window.setTimeout(resolve, ms);
});
/**
 * One piece of a change sentence. Unmarked pieces stay the normal text color.
 */
const marked = (text: string | number, tone?: PlanningDraftChangeTone): PlanningDraftChangeSegment => ({
    text: String(text),
    tone,
});

const PurchasePlanningDetailsView: FunctionComponent = () => {
    const params = useParams() as Record<string, string>;
    const history = useHistory();
    const [_data, setData] = useState<Data>();
    const [_loading, setLoading] = useState(true);
    const [updatingName, setUpdatingName] = useState(false);
    const [importDialogState, setImportDialogState] = useState<PurchasePlanningInformationImportDialogProps>(initialImportDialogState);
    const [changeOrderDialogState, setChangeOrderDialogState] = useState<any>(initialChangeOrderDialog);
    const [quotelDialogState, setQuotelDialogState] = useState<PurchasePlanningInformationQuotesDialogProps>(initialQuoteDialogState);
    const [warrantyDialogState, setWarrantyDialogState] = useState<PurchasePlanningInformationWarrantiesDialogProps>(initialWarrantyDialogState);
    const [updateNameDialogState, setUpdateNameDialogState] = useState<PurchasePlanningInformationUpdateNameDialogProps>(initialUpdateNameDialogState);
    const [removing, setRemoving] = useState(false);
    const [keyword, setKeyword] = useState('');
    const creatingRef = useRef(false);
    const historyRef = useRef<History>(history);
    const operatorAccessLevels = useAccessLevels();
    const purchasingKeyController = usePurchasingKeyController({load: true});
    historyRef.current = history;

    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();

    const sessionId = useMemo(() => params.id === 'new' ? undefined : params.id, [params.id]);
    const loading = useMemo(() => _loading || purchasingKeyController.loadingPurchasingKeyForPlanning, [_loading, purchasingKeyController.loadingPurchasingKeyForPlanning])

    const data = useMemo<Data | undefined>(() => {
        if (!keyword?.length || !_data)
            return _data;
        const normalizedKeyword = keyword.toLowerCase().trim();
        return {
            ..._data,
            dataItems: _data.dataItems.filter(record => {
                const partNumber = record.partNumber?.toLowerCase() ?? '';
                const note = record.note?.toLowerCase() ?? '';
                const category = record.availabilities?.reduce((acc, e) => {
                    if (!!acc) return acc;
                    return e.category?.toLowerCase() ?? '';
                }, '') ?? "";
                return partNumber.includes(normalizedKeyword) || note.includes(normalizedKeyword) || category.includes(normalizedKeyword);
            })
        }
    }, [_data, keyword]);

    const dataItems = useMemo(() => data?.dataItems ?? [], [data?.dataItems]);
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchase-planning-details'});
    const dataRef = useRef<Data | undefined>(_data);
    dataRef.current = _data;
    const bypassGuardRef = useRef(false);
    const saveLockRef = useRef(false);
    const [changesOpen, setChangesOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveEntries, setSaveEntries] = useState<PurchasePlanningSaveEntry[]>([]);
    const [saveOpen, setSaveOpen] = useState(false);
    const [removedVendorsOpen, setRemovedVendorsOpen] = useState(false);
    const [removedVendorGroups, setRemovedVendorGroups] = useState<DroppedVendorDistribution[]>([]);
    const {
        canUndo,
        canRedo,
        changes,
        isServerDirty,
        isAllocationDirty,
        commit,
        undo,
        redo,
        revert,
        reset,
        clearHistory,
        setServerBaseline,
        readBaseline,
    } = usePlanningDraft<DataItemRecord>(_data?.dataItems ?? []);

    const partLabel = useCallback((partNumber?: string) => partNumber?.trim() || t($ => $.untitledPart), [t]);

    /**
     * Applies a working-copy edit and records it for undo.
     */
    const applyItemsChange = useCallback((
        kind: Parameters<typeof commit>[1]['kind'],
        segments: PlanningDraftChangeSegment[],
        updater: (items: DataItemRecord[]) => DataItemRecord[],
    ) => {
        const previous = dataRef.current;
        if (!previous)
            return;
        const nextItems = updater(previous.dataItems);
        commit(previous.dataItems, {kind, label: segments.map(part => part.text).join(''), segments});
        const next = {...previous, dataItems: nextItems};
        dataRef.current = next;
        setData(next);
    }, [commit]);

    /**
     * Moves to the saved session id without asking the user to confirm leaving the draft.
     */
    const replaceSessionRoute = useCallback((nextId?: string) => {
        if (!nextId || nextId === sessionId)
            return;
        creatingRef.current = true;
        bypassGuardRef.current = true;
        requestAnimationFrame(() => {
            historyRef.current.replace(routeFunctions.purchase.planningDetails(nextId));
        });
    }, [sessionId]);

    /**
     * Creates a new temporary session.
     */
    const createNewSession = useCallback(() => {
        const next = {
            id: undefined,
            updateDateTime: undefined,
            title: "New session",
            dataItems: [],
        } satisfies Data;
        dataRef.current = next;
        setData(next);
        reset([]);
        setLoading(false);
    }, [reset])

    /**
     * Fetches the planning session data from the API.
     */
    const fetchSession = useCallback(async (sessionId: string, cancelToken?: CancelToken, purchasingKeyForPlanning?: any) => {
        setLoading(true);
        const response = await BizProductionApiService.v1.purchasePlanning.getInformation({
            id: sessionId,
            purchasingKey: purchasingKeyForPlanning as unknown as string,
        }, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted)
            setLoading(false);
        if (response?.resultFlag) {
            const next = {
                ...response.data,
                dataItems: response?.data?.dataItems
                        ?.map(e => ({
                            ...e,
                            _id: Utils.createUUID(),
                            _changed: false,
                            vendorDistributions: [],
                        }))
                        ?.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                    ?? [],
            } satisfies Data;
            dataRef.current = next;
            setData(next);
            reset(next.dataItems);
        } else if (!response?.resultFlag && !response?.aborted) {
            historyRef.current.replace(routes.purchase.planningList);
        }
    }, [isMounted, reset]);

    /**
     * As soon as the component mounts and with each change of the [sessionId],
     * - fetches the planning session data if sessionId is provided.
     * - If sessionId is not provided, creates a new session.
     */
    useEffect(() => {
        if (purchasingKeyController.loadingPurchasingKeyForPlanning)
            return;
        if (!purchasingKeyController.purchasingKeyForPlanning)
            return historyRef.current.push(historyRef.current.location.pathname.concat(routes.error.accessDeniedForSection));
        if (!sessionId) {
            // means that this is a new session. we should not fetch any data.
            createNewSession();
            return;
        } else if (creatingRef.current) {
            // if the data is already loaded and the sessionId matches, we don't need to fetch it again.
            setLoading(false);
            creatingRef.current = false;
            bypassGuardRef.current = false;
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void fetchSession(sessionId, cancelTokenSource.token, purchasingKeyController.purchasingKeyForPlanning);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [createNewSession, fetchSession, purchasingKeyController.loadingPurchasingKeyForPlanning, purchasingKeyController.purchasingKeyForPlanning, sessionId]);

    /**
     * Applies a CreatePlan response: refreshed rows, a new baseline, and Last calculated.
     */
    const applyBuiltPlan = useCallback((source: Data, updateData?: { id?: string; dataItems?: Array<Partial<DataItemRecord>> | null }) => {
        const merged = mergeServerRows(source.dataItems, updateData?.dataItems, Utils.createUUID);
        const next = {
            ...source,
            id: updateData?.id ?? sessionId,
            updateDateTime: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
            dataItems: merged,
        } satisfies Data;
        dataRef.current = next;
        setData(next);
        clearHistory();
        setServerBaseline(merged);
        replaceSessionRoute(updateData?.id);
    }, [clearHistory, replaceSessionRoute, sessionId, setServerBaseline]);

    /**
     * Builds the planning session.
     */
    const build = useCallback(async (source: Data) => {
        const response = await BizProductionApiService.v1.purchasePlanning.buildPlan({
            id: sessionId,
            purchasingKey: purchasingKeyController.purchasingKeyForPlanning as unknown as string,
            items: source.dataItems.filter(item => !!item.partNumber).map(item => ({
                partNumber: item.partNumber ?? '',
                orderIndex: item.orderIndex ?? 0,
                qty: item.qty ?? 0,
                forecastedQty: item.forecastedQty ?? 0,
                note: item.note ?? '',
            })),
        });
        return response;
    }, [purchasingKeyController.purchasingKeyForPlanning, sessionId]);


    /**
     * Adds a new row to the data grid.
     */
    const addNewRow = useCallback(async ({afterPartChange = false, fromIndex = 0}: {
        afterPartChange?: boolean,
        fromIndex?: number
    } = {}) => {
        const previous = dataRef.current;
        if (afterPartChange && previous) {
            const part = previous.dataItems.slice(fromIndex).find(record => !record.partNumber && !record.qty && !record.forecastedQty && !record._changed);
            if (part) {
                const index = previous.dataItems.indexOf(part, fromIndex);
                if (index !== -1) {
                    const autocompletes = document.querySelectorAll<HTMLElement>(`.${AutocompleteClass} input`);
                    const autocomplete = autocompletes[index];
                    autocomplete?.focus();
                    autocomplete?.scrollIntoView({behavior: 'smooth', block: 'center'});
                    return;
                }
            }
        }
        applyItemsChange('addRow', [
            marked(t($ => $.changePhrase.added), 'primary'),
            marked(t($ => $.changePhrase.aRow)),
        ], items => ([
            ...items,
            {
                _id: Utils.createUUID(),
                _changed: true,
                orderIndex: items.length,
            } satisfies DataItemRecord,
        ]));
        requestAnimationFrame(() => {
            const autocompletes = document.querySelectorAll<HTMLElement>(`.${AutocompleteClass} input`);
            const autocomplete = autocompletes[autocompletes.length - 1];
            autocomplete?.focus();
        });
    }, [applyItemsChange, t])

    /**
     * Removes a row from the data grid by its record ID.
     */
    const removeRow = useCallback(async (recordId: string) => {
        const record = dataRef.current?.dataItems.find(item => item._id === recordId);
        const requiresConfirmation = !!record?.partNumber && (
            (record.qty ?? 0) > 0
            || (record.forecastedQty ?? 0) > 0
            || (record.vendorDistributions?.some(entry => (entry.qty ?? 0) > 0) ?? false)
        );
        if (requiresConfirmation) {
            const confirmed = await new Promise(resolve => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Remove Confirmation',
                    description: `Are you sure you want to remove this record?`,
                    maxWidth: false,
                    fullWidth: false,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return Promise.resolve(true);
                    }
                }));
            })
            if (!confirmed) {
                return;
            }
        }
        applyItemsChange('removeRow', [
            marked(t($ => $.changePhrase.removed)),
            marked(' '),
            marked(partLabel(record?.partNumber), 'disabled'),
        ], items => (
            items.filter(item => item._id !== recordId).map((item, index) => ({...item, orderIndex: index}))
        ));
    }, [applyItemsChange, dispatch, partLabel, t]);

    /**
     * Changes the part of a record.
     */
    const changePart = useCallback(async (recordId: string, part?: PartOption | null) => {
        const previous = dataRef.current?.dataItems.find(item => item._id === recordId);
        if (!previous || previous.partNumber === part?.partNo)
            return;
        let createNewRow = false;
        let index = 0;
        applyItemsChange(
            'updatePart',
            part?.partNo
                ? [
                    marked(t($ => $.changePhrase.partChangedTo)),
                    marked(part.partNo, 'primary'),
                ]
                : [marked(t($ => $.changePhrase.clearedPart))],
            items => items.map((record, recordIndex) => {
                if (record._id !== recordId)
                    return record;
                createNewRow = !!part?.partNo;
                index = recordIndex;
                const qty = !record.qty && !record._changed ? 1 : record.qty;
                return {...record, partNumber: part?.partNo, qty, _changed: true} satisfies DataItemRecord;
            }),
        );
        if (createNewRow) {
            void addNewRow({afterPartChange: true, fromIndex: index});
        }
    }, [addNewRow, applyItemsChange, t]);

    /**
     * Changes the quantity of a record without writing to the server.
     */
    const changeQty = useCallback((recordId: string, qty: number) => {
        const record = dataRef.current?.dataItems.find(item => item._id === recordId);
        if (!record)
            return;
        const nextQty = qty < 0 ? 0 : qty;
        if ((record.qty ?? 0) === nextQty)
            return;
        applyItemsChange('updateQty', [
            marked(t($ => $.changePhrase.qtyFor)),
            marked(partLabel(record.partNumber), 'primary'),
            marked(': '),
            marked(record.qty ?? 0, 'primary'),
            marked(' '),
            marked('→', 'tertiary'),
            marked(' '),
            marked(nextQty, 'secondary'),
        ], items => (
            items.map(item => item._id === recordId ? {...item, qty: nextQty} : item)
        ));
    }, [applyItemsChange, partLabel, t]);

    /**
     * Changes the forecasted quantity of a record without writing to the server.
     */
    const changeForecastedQty = useCallback((recordId: string, qty: number) => {
        const record = dataRef.current?.dataItems.find(item => item._id === recordId);
        if (!record)
            return;
        const nextQty = qty < 0 ? 0 : qty;
        if ((record.forecastedQty ?? 0) === nextQty)
            return;
        applyItemsChange('updateForecastedQty', [
            marked(t($ => $.changePhrase.forecastedQtyFor)),
            marked(partLabel(record.partNumber), 'primary'),
            marked(': '),
            marked(record.forecastedQty ?? 0, 'primary'),
            marked(' '),
            marked('→', 'tertiary'),
            marked(' '),
            marked(nextQty, 'secondary'),
        ], items => (
            items.map(item => item._id === recordId ? {...item, forecastedQty: nextQty} : item)
        ));
    }, [applyItemsChange, partLabel, t]);

    /**
     * Changes the note of a record without writing to the server.
     */
    const changeNote = useCallback((recordId: string, note: string) => {
        const record = dataRef.current?.dataItems.find(item => item._id === recordId);
        if (!record || (record.note ?? '').trim() === note.trim())
            return;
        applyItemsChange('updateNote', [
            marked(t($ => $.changePhrase.note), 'secondary'),
            marked(t($ => $.changePhrase.forPart)),
            marked(partLabel(record.partNumber), 'primary'),
        ], items => (
            items.map(item => item._id === recordId ? {...item, note} : item)
        ));
    }, [applyItemsChange, partLabel, t]);

    /**
     * Sets the session-only quantity allocated to one vendor for a part.
     */
    const changeDistribution = useCallback((recordId: string, vendorCode: string, qty: number) => {
        const record = dataRef.current?.dataItems.find(item => item._id === recordId);
        if (!record)
            return;
        const nextQty = qty < 0 ? 0 : qty;
        const currentQty = record.vendorDistributions?.find(entry => entry.vendorCode === vendorCode)?.qty ?? 0;
        if (currentQty === nextQty)
            return;
        applyItemsChange(
            'distributeQty',
            nextQty > 0
                ? [
                    marked(t($ => $.changePhrase.distributed)),
                    marked(nextQty, 'primary'),
                    marked(t($ => $.changePhrase.of)),
                    marked(partLabel(record.partNumber), 'primary'),
                    marked(t($ => $.changePhrase.to)),
                    marked(vendorCode, 'primary'),
                ]
                : [
                    marked(t($ => $.changePhrase.clearedThe)),
                    marked(vendorCode, 'disabled'),
                    marked(t($ => $.changePhrase.allocationFor)),
                    marked(partLabel(record.partNumber), 'disabled'),
                ],
            items => items.map(item => {
                if (item._id !== recordId)
                    return item;
                const others = (item.vendorDistributions ?? []).filter(entry => entry.vendorCode !== vendorCode);
                return {
                    ...item,
                    vendorDistributions: nextQty > 0 ? [...others, {vendorCode, qty: nextQty}] : others,
                };
            }),
        );
    }, [applyItemsChange, partLabel, t]);

    /**
     * Opens the change order index dialog for a record.
     */
    const openOrderIndexDialog = useCallback((record: DataItemRecord) => {
        const items = dataRef.current?.dataItems ?? [];
        const orderIndexZeroBased = items[0]?.orderIndex === 0;
        setChangeOrderDialogState({
            open: true,
            close: () => setChangeOrderDialogState(initialChangeOrderDialog),
            orderIndex: orderIndexZeroBased ? (record.orderIndex ?? 0) + 1 : (record.orderIndex ?? 0),
            length: items.length,
            message: 'Please set a new order index for the record. The system will shift the order of the records accordingly.',
            reorder: async (newIndex: number) => {
                applyItemsChange('reorder', [
                    marked(t($ => $.changePhrase.reordered), 'secondary'),
                    marked(partLabel(record.partNumber), 'primary'),
                ], rows => (
                    Utils.reorderList(rows, record.orderIndex ?? 0, orderIndexZeroBased ? newIndex - 1 : newIndex, 'orderIndex')
                ));
                return {resultFlag: true, message: undefined}
            },
        })
    }, [applyItemsChange, partLabel, t])

    /**
     * Opens the raw material dialog for a specific raw material.
     */
    const openQuoteDialog = useCallback((partNo: string, year: number) => {
        setQuotelDialogState({
            open: true,
            partNumber: partNo,
            year: year,
            close: () => setQuotelDialogState(initialQuoteDialogState),
        })
    }, []);

    /**
     * Opens the dialog for viewing warranties of a specific part number and vendor code.
     */
    const openWarrantyDialog = useCallback((partNo: string, vendorCode: string) => {
        setWarrantyDialogState({
            open: true,
            partNumber: partNo,
            vendorCode: vendorCode,
            close: () => setWarrantyDialogState(initialWarrantyDialogState),
        })
    }, [])

    /**
     * Opens the dialog for Importing the data.
     */
    const openImportDialog = useCallback(() => {
        setImportDialogState({
            open: true,
            close: () => setImportDialogState(initialImportDialogState),
            onImport: async (imported) => {
                if (!imported?.length)
                    return;
                applyItemsChange('import', [
                    marked(t($ => $.changePhrase.imported), 'primary'),
                    marked(imported.length, 'primary'),
                    marked(t($ => $.changePhrase.rows)),
                ], items => {
                    const newIndex = items.length;
                    return [
                        ...items,
                        ...imported.map((record, index) => ({
                            _id: Utils.createUUID(),
                            _changed: true,
                            orderIndex: newIndex + index,
                            partNumber: record.partNo ?? '',
                            qty: record.quantity ?? 0,
                            forecastedQty: record.forecastedQty,
                            note: record.note ?? '',
                            vendorDistributions: record.vendorDistributions,
                        } satisfies DataItemRecord)),
                    ];
                });
            },
        });
    }, [applyItemsChange, t]);

    /**
     * Updates the name of the planning session.
     */
    const updateName = useCallback(async (name: string) => {
        setUpdatingName(true);
        const response = await BizProductionApiService.v1.planning.updatePlanningProperties({
            id: sessionId!,
            title: name
        });
        if (!isMounted())
            return true;
        if (!response?.aborted)
            setUpdatingName(false);
        if (response?.resultFlag) {
            setData(prevState => {
                if (!prevState) return prevState;
                const next = {
                    ...prevState,
                    title: name,
                };
                dataRef.current = next;
                return next;
            });
        }
        return response?.resultFlag ?? false;
    }, [isMounted, sessionId])


    /**
     * Opens the dialog for updating the name of the planning session.
     */
    const openUpdateNameDialog = useCallback(() => {
        setUpdateNameDialogState({
            open: true,
            close: () => setUpdateNameDialogState(initialUpdateNameDialogState),
            name: data?.title ?? '',
            update: updateName,
        });
    }, [data?.title, updateName]);

    const canBuild = useMemo(() =>
            (_data?.dataItems?.length ?? 0) > 0 &&
            (_data?.dataItems ?? []).some(record => record.partNumber)
        , [_data?.dataItems])

    const canExport = useMemo(() => canBuild, [canBuild]);
    const busy = saving;

    /**
     * Restores the previous draft snapshot.
     */
    const undoChange = useCallback(() => {
        const previous = dataRef.current;
        if (!previous)
            return;
        const snapshot = undo(previous.dataItems);
        if (!snapshot)
            return;
        const next = {...previous, dataItems: snapshot};
        dataRef.current = next;
        setData(next);
    }, [undo]);

    /**
     * Reapplies the most recently undone draft snapshot.
     */
    const redoChange = useCallback(() => {
        const previous = dataRef.current;
        if (!previous)
            return;
        const snapshot = redo(previous.dataItems);
        if (!snapshot)
            return;
        const next = {...previous, dataItems: snapshot};
        dataRef.current = next;
        setData(next);
    }, [redo]);

    /**
     * Restores server-backed fields from the last save and keeps session allocations.
     */
    const revertChange = useCallback(() => {
        if (!sessionId)
            return;
        const previous = dataRef.current;
        if (!previous)
            return;
        const snapshot = revert(previous.dataItems);
        if (!snapshot)
            return;
        const next = {...previous, dataItems: snapshot};
        dataRef.current = next;
        setData(next);
    }, [revert, sessionId]);

    /**
     * Creates or refreshes the plan, then writes quantity and note for the rows that need it.
     */
    const saveDraft = useCallback(async () => {
        if (saving || saveLockRef.current)
            return;
        saveLockRef.current = true;
        let removedVendors: DroppedVendorDistribution[] = [];
        try {
        const source = dataRef.current;
        if (!source?.dataItems.some(record => !!record.partNumber))
            return;
        const invalidRows = source.dataItems.filter(record => !record.partNumber);
        if (invalidRows.length) {
            const confirmed = await new Promise((resolve) => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Invalid Rows',
                    description: `There are a total of ${invalidRows.length} invalid row${invalidRows.length === 1 ? "" : "s"}. If you continue, these rows will be removed prior to calculating.`,
                    confirmButtonText: "Continue",
                    cancelButtonText: "Cancel",
                    maxWidth: false,
                    fullWidth: false,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return Promise.resolve(true);
                    }
                }));
            });
            if (!confirmed || !isMounted())
                return;
            applyItemsChange('removeRow', [
                marked(t($ => $.changePhrase.removed)),
                marked(' '),
                marked(invalidRows.length, 'disabled'),
                marked(t($ => $.changePhrase.invalidRows)),
            ], items => items.filter(record => !!record.partNumber));
        }
        const current = dataRef.current;
        if (!current)
            return;
        const partRows = current.dataItems.filter(item => !!item.partNumber);
        if (!partRows.length)
            return;
        const baseline = readBaseline();
        const detailRows = (sessionId ? partRows.filter(item => {
            const previous = baseline.find(row => (
                (row.orderIndex ?? 0) === (item.orderIndex ?? 0) && row.partNumber === item.partNumber
            ));
            if (!previous)
                return false;
            return (previous.qty ?? 0) !== (item.qty ?? 0) || (previous.note ?? '').trim() !== (item.note ?? '').trim();
        }) : partRows).map(item => ({
            id: `updateDetails:${item.orderIndex ?? 0}:${item.partNumber ?? ''}`,
            partNumber: item.partNumber ?? '',
            orderIndex: item.orderIndex ?? 0,
            qty: item.qty ?? 0,
            note: item.note ?? '',
        }));
        const createId = 'create-plan';
        const entries: PurchasePlanningSaveEntry[] = [
            {
                id: createId,
                label: sessionId ? t($ => $.saveRefreshPlan) : t($ => $.saveCreatePlan),
                status: 'pending',
            },
            ...detailRows.map(row => ({
                id: row.id,
                label: t($ => $.saveUpdateDetails, {part: partLabel(row.partNumber)}),
                status: 'pending' as const,
            })),
        ];
        setSaveEntries(entries);
        setSaveOpen(true);
        setSaving(true);
        const mark = (id: string, status: PurchasePlanningSaveEntry['status'], detail?: string) => {
            setSaveEntries(currentEntries => currentEntries.map(entry => (
                entry.id === id ? {...entry, status, detail} : entry
            )));
        };
            mark(createId, 'saving');
            const response = await build(current);
            if (!isMounted())
                return;
            if (!response?.resultFlag) {
                const detail = response?.message;
                setSaveEntries(currentEntries => currentEntries.map(entry => ({
                    ...entry,
                    status: 'failed',
                    detail,
                })));
                return;
            }
            removedVendors = droppedVendorDistributions(current.dataItems, response.data?.dataItems);
            applyBuiltPlan(current, response.data);
            mark(createId, 'saved');
            const planId = response.data?.id ?? sessionId;
            if (!planId || !detailRows.length)
                return;
            const serverRows = response.data?.dataItems ?? [];
            const usedIndexes = new Set<number>();
            const writes = detailRows.map(row => {
                const match = serverRows.find(item => (
                    item.partNumber === row.partNumber
                    && (item.orderIndex ?? 0) === row.orderIndex
                    && !usedIndexes.has(item.orderIndex ?? 0)
                ));
                const orderIndex = match?.orderIndex ?? row.orderIndex;
                usedIndexes.add(orderIndex);
                return {...row, orderIndex};
            });
            for (let index = 0; index < writes.length; index += DETAIL_UPDATE_BATCH_SIZE) {
                if (index > 0)
                    await wait(DETAIL_UPDATE_BATCH_GAP_MS);
                if (!isMounted())
                    return;
                const batch = writes.slice(index, index + DETAIL_UPDATE_BATCH_SIZE);
                batch.forEach(row => mark(row.id, 'saving'));
                await Promise.all(batch.map(async (row) => {
                    try {
                        const detailResponse = await BizProductionApiService.v1.purchasePlanning.updatePlanDetails({
                            id: planId,
                            purchasingKey: purchasingKeyController.purchasingKeyForPlanning as unknown as string,
                            itemOrderIndex: row.orderIndex,
                            qty: row.qty,
                            note: row.note,
                        });
                        if (!isMounted())
                            return;
                        if (detailResponse?.resultFlag)
                            mark(row.id, 'saved');
                        else
                            mark(row.id, 'failed', detailResponse?.message);
                    } catch (error) {
                        if (!isMounted())
                            return;
                        mark(row.id, 'failed', error instanceof Error ? error.message : undefined);
                    }
                }));
            }
        } finally {
            saveLockRef.current = false;
            if (isMounted()) {
                setSaving(false);
                if (removedVendors.length) {
                    setRemovedVendorGroups(removedVendors);
                    setRemovedVendorsOpen(true);
                }
            }
        }
    }, [applyBuiltPlan, applyItemsChange, build, dispatch, isMounted, partLabel, purchasingKeyController.purchasingKeyForPlanning, readBaseline, saving, sessionId, t]);

    /**
     * Saves the draft from the keyboard. Disabled while a save is already running.
     */
    useEffect(() => {
        const eventListener = function (event: KeyboardEvent) {
            const isMac = Utils.getOsSpecs().isMac;
            const isShortcut = (isMac && event.metaKey) || (!isMac && event.ctrlKey);
            if (isShortcut && event.key.toLowerCase() === 'b') {
                event.preventDefault();
                if (!saveLockRef.current)
                    void saveDraft();
            }
        };
        document.addEventListener('keydown', eventListener);
        return () => {
            document.removeEventListener('keydown', eventListener);
        };
    }, [saveDraft]);

    // +1 for the new-row button and +1 for the totals row
    const totalRowsLength = useMemo(() => dataItems.length + 2, [dataItems?.length])

    const orderedVendorCodes = useMemo(() => {
        const vendorOrders: Record<string, number> = {};
        for (const item of dataItems ?? []) {
            for (const vendor of item.vendorsOfferings ?? []) {
                vendorOrders[vendor?.vendorCode ?? ""] = Math.max(vendorOrders[vendor?.vendorCode ?? ""] ?? 0, item.onOrderItems?.filter(e => e.vendorCode === vendor.vendorCode)?.length ?? 0)
            }
            for (const distribution of item.vendorDistributions ?? []) {
                if (!distribution.vendorCode || vendorOrders[distribution.vendorCode] != null)
                    continue;
                vendorOrders[distribution.vendorCode] = 0;
            }
        }
        const res = Object.entries(vendorOrders).map(([vendorCode, onOrderCount]) => ({
            vendorCode,
            onOrderCount,
        }))
        res.sort((a, b) => b.onOrderCount - a.onOrderCount); // sort by on order count descending
        return res;
    }, [dataItems])

    const orderedYears = useMemo(() => {
        // sort years in descending order
        return Array.from(new Set([
                ...dataItems?.flatMap(e => e.yearQuoteQties?.map(q => q.year) ?? []) ?? [],
                ...dataItems?.flatMap(e => e.yearInvoiceQties?.map(q => q.year) ?? []) ?? [],
                ...dataItems?.flatMap(e => e.yearInvoiceAvgMonthlyQties?.map(q => q.year) ?? []) ?? [],
                ...dataItems?.flatMap(e => e.lostSalesByYear?.map(q => q.year) ?? []) ?? [],
            ].filter(e => !!e && !isNaN(e) && e > 0) as number[]
        )).sort((b, a) => a - b);
    }, [dataItems])

    const dynamicColumns = useMemo(() => {
        const dynamicColumns: DynamicColumn<string | number | JSX.Element | undefined | null>[] = [];

        // dynamic columns:
        // 1. for each warehouse availability:
        // -  Current Available - {warehouseCode}

        const warehouseCodes = Array.from(new Set(dataItems?.flatMap(e => e.availabilities?.map(a => a.whse) ?? []) ?? [])).filter(e => !!e) as string[];
        for (const warehouseCode of warehouseCodes) {
            dynamicColumns.push({
                title: `${warehouseCode ?? ""} Availability`,
                name: columnNames.availability(warehouseCode ?? ""),
                getData: (data) => {
                    let x = data.availabilities?.find(a => a.whse === warehouseCode)?.availability;
                    if (!!x) {
                        x = Utils.roundNumberWithDecimalPoints(x);
                    }
                    return x?.toString() ?? ' ';
                },
                type: DataGridColumnTypes.string,
            } satisfies DynamicColumn);
        }

        // 2. for each vendor:
        //   - Vendor info
        //   - for each on order item: PO, Qty, ETA

        for (const {vendorCode, onOrderCount} of orderedVendorCodes) {
            dynamicColumns.push({
                title: `Vendor ${vendorCode}`,
                name: columnNames.vendor(vendorCode),
                type: DataGridColumnTypes.element,
                width: 150 + 150 + 160 + 30 + (onOrderCount * 190),
                getData: (data) => {
                    const vendorInfo = data.vendorsOfferings?.find(v => v.vendorCode === vendorCode);
                    const onOrderItems = data.onOrderItems?.filter(e => e.vendorCode === vendorCode) ?? [];
                    const hasOffering = !!(vendorInfo?.vendorPartNumber || vendorInfo?.price);
                    const allocated = (data.vendorDistributions ?? []).reduce((sum, entry) => sum + (entry.qty ?? 0), 0);
                    const remaining = (data.qty ?? 0) - allocated;
                    const overAllocated = remaining < 0;
                    const underAllocated = allocated > 0 && remaining > 0;
                    const distributedQty = data.vendorDistributions?.find(entry => entry.vendorCode === vendorCode)?.qty;
                    if (!hasOffering && distributedQty == null)
                        return null;
                    return (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            width: '100%',
                            gap: 1
                        }}>
                            {
                                hasOffering &&
                                <Card
                                    variant={'elevation'}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: 0.5,
                                        boxShadow: 'none',
                                        backgroundColor: 'background.tertiary',
                                        borderRadius: 1,
                                        paddingInline: 2,
                                        paddingBlock: 1,
                                        minWidth: 100,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Part No: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                             color={'text.primary'}>{vendorInfo?.vendorPartNumber ?? 'N/A'}</Typography>
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Price: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                           color={'text.primary'}>{vendorInfo?.price ? formatMoney(vendorInfo.price) : 'N/A'}</Typography>
                                    </Typography>
                                </Card>
                            }
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.5,
                                minWidth: 120
                            }}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    <Typography variant={'body2'}
                                                color={'text.secondary'}>{t($ => $.distributed)}</Typography>
                                    <Tooltip title={t($ => $.allocationNotPersisted)}>
                                        <IconButton size={'small'} aria-label={t($ => $.allocationNotPersisted)}
                                                    sx={{p: 0.25}}>
                                            <InfoOutlined fontSize={'small'}/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {
                                    operatorAccessLevels.packages.purchasing.managePurchasePlanning
                                        ? <DraftNumberInput
                                            value={distributedQty}
                                            disabled={busy}
                                            onCommit={(nextQty) => changeDistribution(data._id, vendorCode, nextQty)}
                                        />
                                        : <Typography variant={'body2'}>{distributedQty ?? 0}</Typography>
                                }
                                {
                                    !hasOffering &&
                                    <Typography variant={'caption'} color={'text.secondary'}>{t($ => $.saveToLoadVendor)}</Typography>
                                }
                                <Typography variant={'body2'}
                                            color={overAllocated ? 'error.main' : underAllocated ? 'warning.main' : 'text.secondary'}>
                                    {overAllocated ? t($ => $.overAllocated, {count: Math.abs(remaining)}) : t($ => $.remaining, {count: remaining})}
                                </Typography>
                            </Box>
                            {
                                ((vendorInfo?.numberOfCredited ?? 0) > 0 || (vendorInfo?.numberOfReturn ?? 0) > 0 || (vendorInfo?.numberOfWarranty ?? 0) > 0) &&
                                <Divider flexItem variant={'middle'} orientation={'vertical'}/>
                            }
                            {
                                ((vendorInfo?.numberOfCredited ?? 0) > 0 || (vendorInfo?.numberOfReturn ?? 0) > 0 || (vendorInfo?.numberOfWarranty ?? 0) > 0) &&
                                <Card
                                    variant={'elevation'}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: 0.5,
                                        boxShadow: 'none',
                                        backgroundColor: 'primary.light',
                                        borderRadius: 1,
                                        paddingInline: 2,
                                        paddingBlock: 1,
                                        minWidth: 100,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => openWarrantyDialog(data.partNumber ?? '', vendorCode)}
                                >
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Credits Qty: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                                 color={'text.primary'}>{vendorInfo?.numberOfCredited ?? 'N/A'}</Typography>
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Returns Qty: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                                 color={'text.primary'}>{vendorInfo?.numberOfReturn ?? 'N/A'}</Typography>
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Warranties Qty: <Typography component={'span'} variant={'body2'}
                                                                    fontWeight={600}
                                                                    color={'text.primary'}>{vendorInfo?.numberOfWarranty ?? 'N/A'}</Typography>
                                    </Typography>
                                </Card>
                            }
                            {
                                onOrderItems.length > 0 &&
                                <Divider flexItem variant={'middle'} orientation={'vertical'}/>
                            }
                            {
                                onOrderItems.map((item, index) => (
                                    <Card key={index} sx={[
                                        {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: 0.5,
                                            boxShadow: 'none',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: 2,
                                            paddingInline: 2,
                                            paddingBlock: 1,
                                        },
                                        (theme) => theme.applyStyles('dark', {
                                            backgroundColor: '#253b50'
                                        }),
                                    ]}>
                                        <Typography variant={'body2'} color={'text.secondary'}>
                                            PO: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                            color={'text.primary'}>{item.poNumber ?? 'N/A'}</Typography>
                                        </Typography>
                                        <Typography variant={'body2'} color={'text.secondary'}>
                                            QTY: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                             color={'text.primary'}>{item.qty ? Utils.roundNumberWithDecimalPoints(item.qty ?? 0) : 'N/A'}</Typography>
                                        </Typography>
                                        <Typography variant={'body2'} color={'text.secondary'}>
                                            ETA: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                             color={'text.primary'}>{item.eta ? DateUtils.serverToLocal(item.eta).format(DateUtils.dateFormatAlternative) : 'N/A'}</Typography>
                                        </Typography>
                                    </Card>
                                ))
                            }
                        </Box>
                    );
                }
            })
        }

        // 3. for each sales: Quotes, Lost Sales YTD, Sales YTD, Avg Monthly Sales YTD

        for (const year of orderedYears) {
            dynamicColumns.push({
                title: `${year} Sales`,
                name: columnNames.sales(year),
                type: DataGridColumnTypes.element,
                width: 320,
                getQuoteDialogInformation: (data) => ({partNumber: data.partNumber ?? '', year: year}),
                getData: (data) => {
                    const yearQuote = data.yearQuoteQties?.find(q => q.year === year);
                    const yearInvoice = data.yearInvoiceQties?.find(q => q.year === year);
                    const yearInvoiceAvgMonthly = data.yearInvoiceAvgMonthlyQties?.find(q => q.year === year);
                    const lostSales = data.lostSalesByYear?.find(q => q.year === year);
                    if (!yearQuote?.qty && !yearInvoice?.qty && !yearInvoiceAvgMonthly?.qty && !lostSales?.qty)
                        return null;
                    return (
                        <Card
                            variant={'elevation'}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                boxShadow: 'none',
                                backgroundColor: 'secondary.light',
                                borderRadius: 1,
                                paddingInline: 2,
                                paddingBlock: 1,
                                cursor: 'pointer'
                            }}
                        >
                            {
                                !!yearQuote?.qty &&
                                <Typography variant={'body2'} color={'text.secondary'} component={'div'}>
                                    Quotes: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                        color={'text.primary'}>{yearQuote?.qty ?? ""}</Typography>
                                </Typography>
                            }
                            {
                                !!yearInvoice?.qty &&
                                <Typography variant={'body2'} color={'text.secondary'} component={'div'}>
                                    Invoices: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                          color={'text.primary'}>{yearInvoice?.qty ?? ""}</Typography>
                                </Typography>
                            }
                            {
                                !!yearInvoiceAvgMonthly?.qty &&
                                <Typography variant={'body2'} color={'text.secondary'} component={'div'}>
                                    Avg Monthly Sales: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                                   color={'text.primary'}>{yearInvoiceAvgMonthly?.qty ? Utils.roundNumberWithDecimalPoints(yearInvoiceAvgMonthly?.qty) : ""}</Typography>
                                </Typography>
                            }
                            {
                                !!lostSales?.qty &&
                                <Typography variant={'body2'} color={'text.secondary'} component={'div'}>
                                    Lost Sales YTD: <Typography component={'span'} variant={'body2'} fontWeight={600}
                                                                color={'text.primary'}>{lostSales?.qty ?? "n/a"}</Typography>
                                </Typography>
                            }
                        </Card>
                    );
                },
            });
        }
        return dynamicColumns;
    }, [busy, changeDistribution, dataItems, openWarrantyDialog, operatorAccessLevels.packages.purchasing.managePurchasePlanning, orderedVendorCodes, orderedYears, t]);

    const columns = useMemo<DataGridColumn[]>(() => [
            {
                name: dataGridColumns.index,
                type: DataGridColumnTypes.number,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                order: 1,
                width: 0,
                resizable: false,
                reorderable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                pinned: true,
            },
            {
                name: dataGridColumns.remove,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                order: 2,
                width: 0,
                resizable: false,
                reorderable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                pinned: true,
            },
            {
                name: dataGridColumns.qty,
                title: "Qty",
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                sortable: false,
                order: 3,
                width: operatorAccessLevels.packages.purchasing.managePurchasePlanning ? 100 : 50,
            },
            {
                name: dataGridColumns.forecastedQty,
                title: t($ => $.forecastedQty),
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                sortable: false,
                order: 4,
                width: operatorAccessLevels.packages.purchasing.managePurchasePlanning ? 130 : 80,
            },
            {
                name: dataGridColumns.partNo,
                title: "Part No",
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                sortable: false,
                order: 5,
                width: operatorAccessLevels.packages.purchasing.managePurchasePlanning ? 200 : 100,
            },
            {
                name: dataGridColumns.category,
                title: "Category",
                type: DataGridColumnTypes.string,
                alignment: DataGridColumnAlignments.left,
                sortable: false,
                order: 6,
            },
            {
                name: dataGridColumns.note,
                title: "Note",
                type: DataGridColumnTypes.string,
                alignment: DataGridColumnAlignments.left,
                sortable: false,
                order: 7,
                width: 250,
            },
            ...dynamicColumns.map((column, index) => ({
                name: column.name,
                title: () => (
                    <Tooltip title={column.description}>
                        <Typography variant={'body2'}>
                            {column.titleElement ?? column.title}
                        </Typography>
                    </Tooltip>
                ),
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                width: {minWidth: Math.max((column.title?.length ?? 0) * 8, column.width ?? 0)},
                sortable: false,
                order: index + 8
            }) as DataGridColumn)
        ] as DataGridColumn[],
        [dynamicColumns, operatorAccessLevels.packages.purchasing.managePurchasePlanning, t])

    const indexStartsFromZero = useMemo(() => dataItems?.[0]?.orderIndex === 0, [dataItems]);

    const dataGridRows = useMemo<DataGridRow[]>(() => {
        const rows: DataGridRow[] = [];
        for (const record of dataItems ?? []) {
            rows.push({
                key: record._id,
                data: record,
                className: classNames({
                    'warning': (record._changed && (!record.partNumber)),
                }),
                cells: {
                    [dataGridColumns.remove]: (
                        <IconButton color={'paper.error'} size={'small'} onClick={() => removeRow(record._id)}>
                            <DeleteOutlined/>
                        </IconButton>
                    ),
                    [dataGridColumns.index]:
                        <IconButton color={'paper.primary'} size={'small'} onClick={() => openOrderIndexDialog(record)}>
                            <Typography variant={'caption'} sx={{
                                minWidth: 24,
                                minHeight: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {indexStartsFromZero ? ((record.orderIndex ?? 0) + 1) : (record.orderIndex ?? 0)}
                            </Typography>
                        </IconButton>,
                    [dataGridColumns.partNo]:
                        operatorAccessLevels.packages.purchasing.managePurchasePlanning
                            ? <Box sx={{width: '100%'}}>
                                <PartAutocomplete
                                    size={'small'}
                                    value={record.partNumber ? {partNo: record.partNumber ?? "", id: record._id} : null}
                                    className={AutocompleteClass}
                                    onChange={(e, val) => changePart(record._id, val)}
                                    disabled={busy}
                                />
                            </Box>
                            : record.partNumber ?? " ",
                    [dataGridColumns.category]: record.availabilities?.reduce((acc, e) => {
                        if (!!acc) return acc;
                        return e.category ?? '';
                    }, '') ?? " ",
                    [dataGridColumns.qty]:
                        operatorAccessLevels.packages.purchasing.managePurchasePlanning
                            ? <DraftNumberInput
                                value={record.qty}
                                disabled={busy}
                                onCommit={(nextQty) => changeQty(record._id, nextQty)}
                            />
                            : record.qty?.toString() ?? " "
                    ,
                    [dataGridColumns.forecastedQty]:
                        operatorAccessLevels.packages.purchasing.managePurchasePlanning
                            ? <DraftNumberInput
                                value={record.forecastedQty}
                                disabled={busy}
                                onCommit={(nextQty) => changeForecastedQty(record._id, nextQty)}
                            />
                            : record.forecastedQty?.toString() ?? " "
                    ,
                    [dataGridColumns.note]:
                        operatorAccessLevels.packages.purchasing.managePurchasePlanning
                            ? <NoteInput
                                record={record}
                                changeNote={changeNote}
                                disabled={busy}
                            />
                            : record.note ?? " "
                    ,
                    ...dynamicColumns.reduce((acc, column) => {
                        const data = column.getData(record);
                        if (column.getQuoteDialogInformation) {
                            const {partNumber, year} = column.getQuoteDialogInformation(record);
                            acc[column.name ?? ""] = (
                                <Typography component={'div'} variant={'body2'} {...dataGridCellTextProps}
                                            onClickCapture={() => openQuoteDialog(partNumber, year)}
                                            sx={{cursor: 'pointer'}}>
                                    {data}
                                </Typography>
                            )
                        } else {
                            acc[column.name ?? ""] = data;
                        }
                        return acc;
                    }, {} as Record<string, DataGridCell | DataGridCellContent>),
                },
            })
        }

        const productRows = dataItems.filter(row => !!row.partNumber?.trim());
        const formatTotal = (value: number) => Utils.roundNumberWithDecimalPoints(value).toString();
        const warehouseCodes = Array.from(new Set(dataItems.flatMap(row => row.availabilities?.map(entry => entry.whse) ?? []))).filter((code): code is string => !!code);
        const totalsCells: Record<string, DataGridCell | DataGridCellContent> = {
            [dataGridColumns.remove]: ' ',
            [dataGridColumns.index]: (
                <Typography variant={'body2'} sx={{fontFamily: 'Roboto'}}>{t($ => $.totals)}</Typography>
            ),
            [dataGridColumns.qty]: (
                <Typography
                    variant={'body2'}>{formatTotal(dataItems.reduce((sum, row) => sum + (row.qty ?? 0), 0))}</Typography>
            ),
            [dataGridColumns.forecastedQty]: (
                <Typography
                    variant={'body2'}>{formatTotal(dataItems.reduce((sum, row) => sum + (row.forecastedQty ?? 0), 0))}</Typography>
            ),
            [dataGridColumns.partNo]: (
                <Card
                    variant={'outlined'}
                    sx={{
                        boxShadow: 'none',
                        backgroundColor: 'background.tertiary',
                        borderColor: 'transparent',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.25,
                        paddingBlock: 0.5,
                        paddingInline: 1,
                    }}
                >
                    <Typography variant={'body2'}>{t($ => $.lineCount, {count: productRows.length})}</Typography>
                    <Typography
                        variant={'body2'}>{t($ => $.distinctCount, {count: new Set(productRows.map(row => row.partNumber!.trim().toLowerCase())).size})}</Typography>
                </Card>
            ),
            [dataGridColumns.category]: ' ',
            [dataGridColumns.note]: ' ',
        };
        for (const warehouseCode of warehouseCodes) {
            const availability = dataItems.reduce((sum, row) => sum + (row.availabilities?.find(entry => entry.whse === warehouseCode)?.availability ?? 0), 0);
            totalsCells[columnNames.availability(warehouseCode)] = formatTotal(availability);
        }
        for (const {vendorCode} of orderedVendorCodes) {
            const distributed = dataItems.reduce((sum, row) => sum + (row.vendorDistributions?.find(entry => entry.vendorCode === vendorCode)?.qty ?? 0), 0);
            totalsCells[columnNames.vendor(vendorCode)] = (
                <Card
                    variant={'outlined'}
                    sx={{
                        boxShadow: 'none',
                        backgroundColor: 'background.tertiary',
                        borderColor: 'transparent',
                        paddingBlock: 0.5,
                        paddingInline: 1,
                    }}
                >
                    <Typography
                        variant={'body2'}>{t($ => $.distributedTotal, {count: Utils.roundNumberWithDecimalPoints(distributed)})}</Typography>
                </Card>
            );
        }
        for (const year of orderedYears) {
            const quotes = dataItems.reduce((sum, row) => sum + (row.yearQuoteQties?.find(entry => entry.year === year)?.qty ?? 0), 0);
            const invoices = dataItems.reduce((sum, row) => sum + (row.yearInvoiceQties?.find(entry => entry.year === year)?.qty ?? 0), 0);
            const avgMonthly = dataItems.reduce((sum, row) => sum + (row.yearInvoiceAvgMonthlyQties?.find(entry => entry.year === year)?.qty ?? 0), 0);
            const lostSales = dataItems.reduce((sum, row) => sum + (row.lostSalesByYear?.find(entry => entry.year === year)?.qty ?? 0), 0);
            totalsCells[columnNames.sales(year)] = (
                <Card
                    variant={'outlined'}
                    sx={{
                        boxShadow: 'none',
                        backgroundColor: 'secondary.light',
                        borderColor: 'transparent',
                        display: 'grid',
                        gridTemplateColumns: 'max-content max-content',
                        columnGap: 2,
                        rowGap: 0.25,
                        paddingBlock: 0.5,
                        paddingInline: 1,
                    }}
                >
                    <Typography variant={'body2'}
                                sx={{whiteSpace: 'nowrap'}}>{t($ => $.quotes)}: {formatTotal(quotes)}</Typography>
                    <Typography variant={'body2'}
                                sx={{whiteSpace: 'nowrap'}}>{t($ => $.invoices)}: {formatTotal(invoices)}</Typography>
                    <Typography variant={'body2'}
                                sx={{whiteSpace: 'nowrap'}}>{t($ => $.avgMonthlySales)}: {formatTotal(avgMonthly)}</Typography>
                    <Typography variant={'body2'}
                                sx={{whiteSpace: 'nowrap'}}>{t($ => $.lostSalesYtd)}: {formatTotal(lostSales)}</Typography>
                </Card>
            );
        }
        for (const column of dynamicColumns) {
            if (totalsCells[column.name ?? ''] == null)
                totalsCells[column.name ?? ''] = ' ';
        }
        const totalsRow = {
            key: 'totals',
            data: null,
            stickToBottom: true,
            className: 'data-grid-footer-row',
            cells: totalsCells,
        } satisfies DataGridRow;

        return [
            ...rows,
            {
                key: "add-new-row",
                data: null,
                cells: {
                    [dataGridColumns.remove]: ' ',
                    [dataGridColumns.index]: ' ',
                    [dataGridColumns.qty]: ' ',
                    [dataGridColumns.forecastedQty]: ' ',
                    [dataGridColumns.partNo]: (
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            size={'small'}
                            onClick={() => addNewRow()}
                            sx={{width: '100%'}}
                            startIcon={<AddOutlined/>}
                        >
                            New Row
                        </Button>
                    ),
                    [dataGridColumns.category]: ' ',
                    [dataGridColumns.note]: ' ',
                    ...dynamicColumns.reduce<Record<string, DataGridCell | DataGridCellContent>>((acc, column) => {
                        acc[column.name ?? ""] = " ";
                        return acc;
                    }, {}),
                },
            } satisfies DataGridRow,
            totalsRow,
        ];
    }, [addNewRow, busy, changeForecastedQty, changeNote, changePart, changeQty, dataItems, dynamicColumns, indexStartsFromZero, openOrderIndexDialog, openQuoteDialog, operatorAccessLevels.packages.purchasing.managePurchasePlanning, orderedVendorCodes, orderedYears, removeRow, t])

    /**
     * Fetches the exporting specific columns for the planning session.
     */
    const getExportColumns = useCallback((): DynamicColumn[] => {
        const dynamicColumns: DynamicColumn[] = [];

        // dynamic columns:
        // 1. for each warehouse availability:
        // -  Current Available - {warehouseCode}

        const warehouseCodes = Array.from(new Set(dataItems?.flatMap(e => e.availabilities?.map(a => a.whse) ?? []) ?? [])).filter(e => !!e) as string[];
        for (const warehouseCode of warehouseCodes) {
            dynamicColumns.push({
                title: `Current Available - ${warehouseCode ?? ""}`,
                name: columnNames.availability(warehouseCode ?? ""),
                getData: (data) => {
                    let x = data.availabilities?.find(a => a.whse === warehouseCode)?.availability;
                    if (!!x) {
                        x = Utils.roundNumberWithDecimalPoints(x);
                    }
                    return x?.toString() ?? ' ';
                },
                type: DataGridColumnTypes.string,
            } satisfies DynamicColumn);
        }

        // 2. for each vendor:
        //    - on order - {vendorCode}
        //    - on order ETA - {vendorCode}

        const onOrderVendorCodes = Array.from(new Set(dataItems?.flatMap(e => e.onOrderItems?.map(v => v.vendorCode) ?? []) ?? [])).filter(e => !!e) as string[];
        for (const vendorCode of onOrderVendorCodes) {
            const orderCount = (dataItems ?? []).reduce((max, item) => (
                Math.max(max, item.onOrderItems?.filter(entry => entry.vendorCode === vendorCode).length ?? 0)
            ), 0);
            for (let position = 0; position < orderCount; position += 1) {
                const numbered = orderCount > 1;
                const orderLabel = (kind: string) => numbered
                    ? `On Order ${kind} ${position + 1} - ${vendorCode}`
                    : `On Order ${kind} - ${vendorCode}`;
                const nameSuffix = numbered ? `-${position + 1}` : '';
                const orderAt = (data: DataItemRecord) => (
                    data.onOrderItems?.filter(entry => entry.vendorCode === vendorCode)[position]
                );
                dynamicColumns.push({
                    title: orderLabel('PO'),
                    name: `${columnNames.onOrderPo(vendorCode)}${nameSuffix}`,
                    getData: (data) => orderAt(data)?.poNumber?.toString() ?? ' ',
                    type: DataGridColumnTypes.string,
                } satisfies DynamicColumn);
                dynamicColumns.push({
                    title: orderLabel('QTY'),
                    name: `${columnNames.onOrderQty(vendorCode)}${nameSuffix}`,
                    getData: (data) => {
                        const qty = orderAt(data)?.qty;
                        if (qty == null || Number.isNaN(qty))
                            return ' ';
                        return Utils.roundNumberWithDecimalPoints(qty).toString();
                    },
                    type: DataGridColumnTypes.string,
                } satisfies DynamicColumn);
                dynamicColumns.push({
                    title: orderLabel('ETA'),
                    name: `${columnNames.onOrderEta(vendorCode)}${nameSuffix}`,
                    getData: (data) => orderAt(data)?.eta ?? ' ',
                    type: DataGridColumnTypes.date,
                } satisfies DynamicColumn);
            }
        }

        // 3. for each vendor:
        //   - {vendorCode} - Part Number format
        //   - {vendorCode} - price

        const vendorCodes = Array.from(new Set([
            ...dataItems?.flatMap(e => e.vendorsOfferings?.map(v => v.vendorCode) ?? []) ?? [],
            ...dataItems?.flatMap(e => e.vendorDistributions?.map(v => v.vendorCode) ?? []) ?? [],
        ])).filter(e => !!e) as string[];
        for (const vendorCode of vendorCodes) {
            dynamicColumns.push({
                title: `Part No - ${vendorCode ?? ""}`,
                name: columnNames.partNoFormat(vendorCode ?? ""),
                getData: (data) => data.vendorsOfferings?.find(v => v.vendorCode === vendorCode)?.vendorPartNumber ?? ' ',
                type: DataGridColumnTypes.string,
            } satisfies DynamicColumn);

            dynamicColumns.push({
                title: `Price - ${vendorCode ?? ""}`,
                name: columnNames.price(vendorCode ?? ""),
                getData: (data) => data.vendorsOfferings?.find(v => v.vendorCode === vendorCode)?.price ?? ' ',
                type: DataGridColumnTypes.money,
            } satisfies DynamicColumn);

            dynamicColumns.push({
                title: distributedQtyColumnTitle(vendorCode),
                name: `distributed-qty-${vendorCode}`,
                getData: (data) => {
                    const qty = data.vendorDistributions?.find(entry => entry.vendorCode === vendorCode)?.qty;
                    if (!qty)
                        return ' ';
                    return Utils.roundNumberWithDecimalPoints(qty).toString();
                },
                type: DataGridColumnTypes.number,
            } satisfies DynamicColumn);
        }

        // 4. for each sales:
        //   - Quotes {year}
        //   - Lost Sales YTD {year}
        //   - Sales YTD {year}
        //   - Avg Monthly Sales YTD {year}

        const years = Array.from(new Set([
                ...dataItems?.flatMap(e => e.yearQuoteQties?.map(q => q.year) ?? []) ?? [],
                ...dataItems?.flatMap(e => e.yearInvoiceQties?.map(q => q.year) ?? []) ?? [],
                ...dataItems?.flatMap(e => e.yearInvoiceAvgMonthlyQties?.map(q => q.year) ?? []) ?? [],
                ...dataItems?.flatMap(e => e.lostSalesByYear?.map(q => q.year) ?? []) ?? [],
            ].filter(e => !!e && !isNaN(e) && e > 0) as number[]
        )).sort((b, a) => a - b); // sort years in descending order

        for (const year of years) {
            dynamicColumns.push({
                title: `Quotes ${year}`,
                name: columnNames.quotes(year),
                getData: (data) => {
                    let x = data.yearQuoteQties?.find(s => s.year === year)?.qty;
                    if (!!x) {
                        x = Utils.roundNumberWithDecimalPoints(x);
                    }
                    return x?.toString() ?? ' ';
                },
                type: DataGridColumnTypes.string,
            } satisfies DynamicColumn);

            dynamicColumns.push({
                title: `Lost Sales YTD ${year}`,
                name: columnNames.lostSalesYtd(year),
                getData: (data) => {
                    let x = data.lostSalesByYear?.find(s => s.year === year)?.qty;
                    if (!!x) {
                        x = Utils.roundNumberWithDecimalPoints(x);
                    }
                    return x?.toString() ?? ' ';
                },
                type: DataGridColumnTypes.number,
            } satisfies DynamicColumn);

            dynamicColumns.push({
                title: `Sales YTD ${year}`,
                name: columnNames.salesYtd(year),
                getData: (data) => {
                    let x = data.yearInvoiceQties?.find(s => s.year === year)?.qty;
                    if (!!x) {
                        x = Utils.roundNumberWithDecimalPoints(x);
                    }
                    return x?.toString() ?? ' ';
                },
                type: DataGridColumnTypes.number,
            } satisfies DynamicColumn);

            dynamicColumns.push({
                title: `Avg Monthly Sales YTD ${year}`,
                name: columnNames.avgMonthlySalesYtd(year),
                getData: (data) => {
                    let x = data.yearInvoiceAvgMonthlyQties?.find(s => s.year === year)?.qty;
                    if (!!x) {
                        x = Utils.roundNumberWithDecimalPoints(x);
                    }
                    return x?.toString() ?? ' ';
                },
                type: DataGridColumnTypes.number,
            } satisfies DynamicColumn);
        }

        return dynamicColumns;
    }, [dataItems]);

    /**
     * Exports the planning session data to a CSV file.
     */
    const exportData = useCallback(async () => {
        const rows: Record<string, string>[] = [];
        for (const record of dataItems ?? []) {
            rows.push({
                " ": (indexStartsFromZero ? ((record.orderIndex ?? 0) + 1) : (record.orderIndex ?? 0)).toString(),
                "Part Number": record.partNumber ?? " ",
                "Category": record.availabilities?.reduce((acc, e) => {
                    if (!!acc) return acc;
                    return e.category ?? '';
                }, '') ?? "",
                "Qty": (record.qty ?? 0).toString(),
                [t($ => $.forecastedQty)]: (record.forecastedQty ?? 0).toString(),
                "Note": record.note ?? " ",
                ...getExportColumns().reduce((acc, column) => {
                    acc[column.title ?? ""] = column.getData(record)?.toString() ?? " ";
                    return acc;
                }, {} as Record<string, string>),
            });
        }
        await Utils.exportCsvFile(rows, {fileTitle: `Purchase Planning - ${data?.title ?? "Temporary Session"}`})
    }, [dataItems, data?.title, getExportColumns, indexStartsFromZero, t])

    const buildKeybind = useMemo(() => {
        const specs = Utils.getOsSpecs();
        if (specs.isMac) {
            return "⌘ + B";
        } else if (specs.isWindows || specs.isLinux) {
            return "Ctrl + B";
        } else {
            return "";
        }
    }, [])

    const inMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
    const compactFooterActions = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    const showPlanningFooter = !loading
        && operatorAccessLevels.packages.purchasing.managePurchasePlanning
        && (canBuild || canUndo || canRedo || isServerDirty || isAllocationDirty);
    const paperRef = useRef<HTMLDivElement>(null);

    /**
     * Caps the grid scrollport to the paper's leftover space so the data-grid's
     * inline row-based height cannot push the totals row past the footer.
     */
    useLayoutEffect(() => {
        const paper = paperRef.current;
        if (!paper || inMobile)
            return;

        const applyMaxHeight = () => {
            const layout = paper.querySelector<HTMLElement>('.data-grid-layout');
            if (!layout)
                return;
            const search = paper.querySelector<HTMLElement>('.search-input');
            const divider = paper.querySelector<HTMLElement>('hr');
            const toolbar = paper.querySelector<HTMLElement>('.data-grid-toolbar');
            const usedAbove = (search?.offsetHeight ?? 0) + (divider?.offsetHeight ?? 0) + (toolbar?.offsetHeight ?? 0);
            const available = Math.max(paper.clientHeight - usedAbove, 0);
            layout.style.maxHeight = `${available}px`;
        };

        applyMaxHeight();
        const observer = new ResizeObserver(applyMaxHeight);
        observer.observe(paper);
        return () => observer.disconnect();
    }, [inMobile, showPlanningFooter, loading, dataItems.length]);


    /**
     * Removes the planning session from the list.
     */
    const removePlanningSession = useCallback(async (sessionId: Data['id']) => {
        setRemoving(true);
        const response = await BizProductionApiService.v1.planning.deletePlanning({id: sessionId!});
        if (!isMounted())
            return;
        setRemoving(false);
        if (response?.resultFlag) {
            bypassGuardRef.current = true;
            historyRef.current.replace(routes.purchase.planningList);
        }
        return response?.resultFlag ?? false;
    }, [isMounted]);

    /**
     * Gets the confirmation for removing the planning session.
     */
    const getRemoveConfirmation = useCallback(async () => {
        const confirmed = await new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Confirm Removal',
                description: `Are you sure you want to remove this planning session? This action cannot be undone.`,
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                maxWidth: false,
                fullWidth: false,
                onClose: (confirmed) => {
                    resolve(confirmed);
                    return Promise.resolve(true);
                }
            }));
        })
        if (confirmed) {
            void removePlanningSession(data?.id!);
        }
    }, [data?.id, dispatch, removePlanningSession]);

    return (
        <>
            <Box
                className={'panel'}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    ...(!inMobile && {
                        height: showPlanningFooter
                            ? 'calc(100vh - 32px - 48px - 64px - 64px)'
                            : 'calc(100vh - 32px - 48px - 64px)',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 2,
                        marginBlockEnd: 4,
                        flexShrink: 0,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <IconButton
                            color={'paper.primary'}
                            // @ts-ignore
                            component={Link}
                            to={routes.purchase.planningList}
                        >
                            <KeyboardArrowLeftRounded/>
                        </IconButton>
                        {
                            loading &&
                            <Skeleton variant={'text'} sx={{transform: 'none'}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Plan: Temporary Session
                                    </Typography>
                                    {
                                        sessionId &&
                                        operatorAccessLevels.packages.purchasing.managePurchasePlanning &&
                                        <IconButton color={'paper.primary'} size={'small'} disabled>
                                            <EditOutlined/>
                                        </IconButton>
                                    }
                                </Box>
                            </Skeleton>
                        }
                        {
                            !loading &&
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    {
                                        sessionId
                                            ? `Plan: ${data?.title ?? '--'}`
                                            : "Temporary Session (New)"
                                    }
                                </Typography>
                                {
                                    sessionId &&
                                    <Tooltip title={'Update the name'}>
                                        <IconButton color={'paper.primary'} size={'small'}
                                                    onClick={openUpdateNameDialog}>
                                            {
                                                updatingName
                                                    ? <CircularProgress size={24} color={'primary'}/>
                                                    : <EditOutlined/>
                                            }
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Box>
                        }
                    </Box>
                    {
                        !loading &&
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end'}}>
                            {
                                operatorAccessLevels.packages.purchasing.managePurchasePlanning &&
                                <Button
                                    disabled={busy}
                                    variant={'outlined'}
                                    color={'primary'}
                                    sx={{paddingBlock: 0.5, paddingInline: 1.5}}
                                    onClick={openImportDialog}
                                    startIcon={<Excel/>}
                                >
                                    Import Data
                                </Button>
                            }

                            {
                                canExport &&
                                operatorAccessLevels.packages.purchasing.exportPurchasePlanning &&
                                <Button
                                    disabled={busy}
                                    variant={'outlined'}
                                    color={'primary'}
                                    sx={{paddingBlock: 0.5, paddingInline: 1.5}}
                                    onClick={exportData}
                                >
                                    Export Data
                                </Button>
                            }
                            {
                                sessionId &&
                                operatorAccessLevels.packages.purchasing.managePurchasePlanning &&
                                <Button
                                    variant={'outlined'}
                                    color={'error'}
                                    size={'medium'}
                                    disabled={busy || removing}
                                    onClick={getRemoveConfirmation}
                                    startIcon={removing ? <CircularProgress size={16} color={'inherit'}/> :
                                        <DeleteOutlined/>}
                                >
                                    {removing ? "Removing..." : "Remove"}
                                </Button>
                            }
                        </Box>
                    }
                </Box>
                <Paper ref={paperRef}
                       sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                    <SearchInput
                        initialValue={keyword}
                        search={setKeyword}
                        loading={loading || busy}
                        disabled={busy || loading}
                        autoSearch
                        label={'Search'}
                        placeholder={'search among part numbers, categories, notes...'}
                        setEnteredKeyword={null}
                        iconButtonProps={null}
                        width={null}
                        paperProps={null}
                    />
                    <Divider/>
                    <PartnerDataGrid
                        initialLayoutState={{columns: columns}}
                        columns={columns}
                        rows={dataGridRows}
                        classNames={{
                            container: 'planning-information-data-grid-container',
                            layout: "virtualized-grid-list",
                        }}
                        hideFooter
                        hideExporting
                        useVirtualizedRows
                        useVirtualizedColumns
                        visibleRows={8}
                        state={{
                            pagination: {currentPage: 1, pageSize: totalRowsLength, length: totalRowsLength},
                            loading: {state: loading || busy},
                        }}
                    />
                </Paper>
            </Box>

            {
                showPlanningFooter &&
                <PanelFooter>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        width: '100%',
                        gap: 1,
                        padding: 1,
                        flexWrap: 'wrap',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 1,
                            flexWrap: 'wrap'
                        }}>
                            <FooterAction
                                label={t($ => $.undo)}
                                icon={<Undo/>}
                                compact={compactFooterActions}
                                disabled={!canUndo || busy}
                                onClick={undoChange}
                            />
                            <FooterAction
                                label={t($ => $.redo)}
                                icon={<Redo/>}
                                compact={compactFooterActions}
                                disabled={!canRedo || busy}
                                onClick={redoChange}
                            />
                            <FooterAction
                                label={t($ => $.changesCount, {count: changes.length})}
                                icon={
                                    <Badge
                                        color={'primary'}
                                        badgeContent={changes.length}
                                        invisible={!compactFooterActions || changes.length === 0}
                                    >
                                        <HistoryOutlined/>
                                    </Badge>
                                }
                                compact={compactFooterActions}
                                disabled={busy}
                                onClick={() => setChangesOpen(true)}
                            />
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 1,
                            flexWrap: 'wrap'
                        }}>
                            {
                                !!sessionId &&
                                <FooterAction
                                    label={t($ => $.revert)}
                                    icon={<RestoreOutlined/>}
                                    compact={compactFooterActions}
                                    disabled={!isServerDirty || busy}
                                    onClick={revertChange}
                                />
                            }
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5}}>
                                <Button
                                    size={'medium'}
                                    variant={'contained'}
                                    color={'primary'}
                                    disabled={!canBuild || busy}
                                    onClick={() => void saveDraft()}
                                >
                                    {saving
                                        ? t($ => $.saving)
                                        : buildKeybind.length
                                            ? t($ => $.saveDraftKeybind, {keybind: buildKeybind})
                                            : t($ => $.save)}
                                </Button>
                                {
                                    !saving && !!data?.updateDateTime &&
                                    <LastBuiltDate date={data?.updateDateTime}/>
                                }
                            </Box>
                        </Box>
                    </Box>
                </PanelFooter>
            }
            <RouteLeavingGuard
                when={isServerDirty && !bypassGuardRef.current}
                shouldBlockNavigation={() => !bypassGuardRef.current && isServerDirty}
                navigate={(location, action) => {
                    if (action === 'POP')
                        history.goBack();
                    else if (action === 'REPLACE')
                        history.replace(location);
                    else
                        history.push(location);
                }}
                confirmationTitle={t($ => $.leaveTitle)}
                confirmationMessage={t($ => $.leaveMessage)}
            />
            <PurchasePlanningChangesDialog
                open={changesOpen}
                setOpen={setChangesOpen}
                changes={changes}
            />
            <PurchasePlanningSaveDialog
                open={saveOpen}
                setOpen={setSaveOpen}
                saving={saving}
                entries={saveEntries}
            />
            <PurchasePlanningRemovedVendorsDialog
                open={removedVendorsOpen}
                setOpen={setRemovedVendorsOpen}
                groups={removedVendorGroups}
            />
            <PurchasePlanningInformationImportDialog {...importDialogState}/>
            <ChangeOrderDialog {...changeOrderDialogState}/>
            <PurchasePlanningDetailsQuotesDialog {...quotelDialogState} />
            <PurchasePlanningDetailsWarrantiesDialog {...warrantyDialogState}/>
            <PurchasePlanningInformationUpdateNameDialog {...updateNameDialogState} />
        </>
    );
}

export default PurchasePlanningDetailsView;

interface FooterActionProps {
    label: string;
    icon: React.ReactNode;
    compact: boolean;
    disabled?: boolean;
    onClick: () => void;
}

const FooterAction: FunctionComponent<FooterActionProps> = (props) => (
    <Tooltip title={props.compact ? props.label : ''}>
        <span>
            <Button
                size={'medium'}
                variant={'outlined'}
                disabled={props.disabled}
                onClick={props.onClick}
                startIcon={props.compact ? undefined : props.icon}
                aria-label={props.label}
                sx={props.compact ? {minWidth: 40, px: 1.25, overflow: 'visible'} : undefined}
            >
                {props.compact ? props.icon : props.label}
            </Button>
        </span>
    </Tooltip>
);

const LastBuiltDate: FunctionComponent<{ date?: string }> = ({date}) => {
    const [, setTicker] = useState(false);
    const localDate = useMemo(() => DateUtils.serverToLocal(date), [date]);

    /**
     * Sets up an interval to update the local date every minute.
     */
    useEffect(() => {
        const interval = setInterval(() => setTicker(prev => !prev), 60000); // Update every minute
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <Tooltip arrow title={localDate ? `Last calculated on ${localDate?.toDate()?.toLocaleString()}` : null}>
                <Typography variant={'caption'} color={'text.secondary'}>
                    Last calculated: {localDate ? `${localDate.fromNow()}` : 'N/A'}
                </Typography>
            </Tooltip>
        </>
    );
}

interface DraftNumberInputProps {
    value?: number;
    disabled: boolean;
    onCommit: (value: number) => void;
}

const DraftNumberInput: FunctionComponent<DraftNumberInputProps> = (props) => {
    const ref = useRef<HTMLInputElement>(null);

    /**
     * Keeps the uncontrolled input aligned with the draft value after undo, redo, or revert.
     */
    useEffect(() => {
        const input = ref.current;
        if (input) {
            input.value = props.value?.toString() ?? '';
        }
    }, [props.value])

    /**
     * Commits the number when the input loses focus.
     */
    const onBlur = useCallback<Required<InputBaseProps>['onBlur']>((e) => {
        let nextValue = Number(e.target.value);
        if (Number.isNaN(nextValue))
            return;
        if (nextValue < 0) {
            e.target.value = '0';
            nextValue = 0;
        }
        props.onCommit(nextValue);
    }, [props]);

    return (
        <TextField
            type={'number'}
            size={'small'}
            fullWidth
            inputRef={ref}
            defaultValue={props.value ?? ''}
            onBlur={onBlur}
            {...dataGridCellTextProps}
            disabled={props.disabled}
            inputProps={{min: 0}}
            InputProps={{sx: {fontSize: '0.875rem'}}}
        />
    );
}

interface NoteInputProps {
    record: DataItemRecord;
    changeNote: (recordId: string, note: string) => void;
    disabled: boolean;
}

const NoteInput: FunctionComponent<NoteInputProps> = (props) => {
    const {changeNote} = props;
    const ref = useRef<HTMLInputElement>(null);

    /**
     * Keeps the uncontrolled input aligned with the draft note after undo, redo, or revert.
     */
    useEffect(() => {
        const input = ref.current;
        if (input) {
            input.value = props.record?.note?.toString() ?? '';
        }
    }, [props.record?.note])

    /**
     * Commits the note when the input loses focus.
     */
    const onBlur = useCallback<Required<InputBaseProps>['onBlur']>((e) => {
        changeNote(props.record._id, e.target.value ?? '');
    }, [changeNote, props.record._id]);

    return (
        <TextField
            type={'text'}
            size={'small'}
            fullWidth
            inputRef={ref}
            defaultValue={props.record.note ?? ''}
            minRows={1}
            maxRows={3}
            onBlur={onBlur}
            disabled={props.disabled}
            InputProps={{sx: {fontSize: '0.875rem'}}}
            {...dataGridCellTextProps}
        />
    );
}